import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react'
import { getStartLocation } from '../utils/LocationUtils'
import miscellaneousVehicle from '../../../../src/features/search/Results/utils/miscellaneousVehicle'
import { StoreInstances } from '../../../../src/store/StoreInstancesContainer'
import { updatePartInfo, isVehicleComplete } from '../Utils'
import {
  productsMatch,
  autoSelectLocation,
  getLocationById,
  allowOrderWithoutAvailableInventory,
  isSameVehicle,
} from '../utils/Utils_testable'
import {
  InventoryDisplay,
  ProductLocationModel,
  ProductModel,
} from 'interfaces'
import { Vehicle } from '../../../../src/store/models/Vehicles'
import { NonCachedSearchType } from 'interfaces'
import { useSearchStore } from '../../../../src/store/search/SearchStore'
import { useUiStateStore } from '../../../../src/store/uiState/UiStateStore'
import { useUserStore } from '../../../../src/store/user/UserStore'

import history from '../../../../src/helpers/history'
import { LocationType } from '../../../../src/helpers/locationType'
import {
  ABSOLUTE_MAX_QUANTITY,
  MY_PLACE_SELLER,
  Translate,
  Translate2,
} from 'common'
import { ISeller } from '../../../../src/services/MultiSellerServicesProvider'
import { show } from '@ebay/nice-modal-react'
import { StoreSelectorDrawer } from '../../../../src/features/locations/change/StoreSelectorDrawer'
import { AddToCartDrawer } from '../../../../src/features/locations/addPart/AddToCartDrawer'
import {
  removeProductThunk,
  setQtyAtLocationBulk,
  updateBundleFulfilmentThunk,
} from '../utils/Utils'
import { useSelector } from 'react-redux'
import { RootState, store } from 'store'
import { getInCartProductQty, isProductInCart } from '../store/cartSelectors'
import ShowNotification from 'src/ui-components/modals/ToastNotifcationNotiStack'
import { ToastNotificationVariant } from 'src/ui-components/util/Interfaces'
import { SellerCartVehicle } from '../store/ISellerCart'
import { hawkSearchProvider } from '../../../../src/config/analytics/core/providers/HawkSearchServiceProvider'
import { prepareEventData } from '../../../../src/helpers/HawkSearchEventHelpers'

export const viewCartLink = {
  text: Translate2('viewCart'),
  action: (): void => {
    history.navigate('/cart')
  },
}

interface ICartProductProps {
  product: ProductModel
  vehicle: Vehicle
  locationId?: string
  sellerDetails?: ISeller
}

interface UpdateQuantityResponse {
  prevLocation: ProductLocationModel
  newLocation: ProductLocationModel
  updatedLocations: ProductLocationModel[]
}

export interface ICartProductData {
  updateQuantity: (
    qty: number,
    location: ProductLocationModel
  ) => Promise<UpdateQuantityResponse>
  numInCart: number
  setNumInCart: Dispatch<SetStateAction<number>>
  inCart: boolean
  activeLocation: ProductLocationModel
  currentQty: number
  setCurrentQty: Dispatch<SetStateAction<number>>
  setPriceSpinner: Dispatch<SetStateAction<boolean>>
  showLocationSelector: () => void
  handleAddVehiclePartToCart: (
    vehicle: Vehicle,
    location?: ProductLocationModel,
    qty?: number
  ) => Promise<void>
  priceSpinner: boolean
  initialSelectorQty: number
  inCartText: string
  disableAddToCart: boolean
  isZeroCost: boolean
  yourCostAfterPriceBreaks: () => number
  setActiveLocation: Dispatch<SetStateAction<ProductLocationModel>>
  product: ProductModel
  getInCartQtyByLocation: (location: ProductLocationModel) => number
  onChangeLocation: (selectedLocation: ProductLocationModel) => void
  inStockSomewhere: boolean
  orderIfNotAvailable: boolean
  invDisplayState: InventoryDisplay
  getNonPrimaryLocationsQtyAvailable: number
  getPrimaryLocationQtyAvailable: { qty: number; called: string }
  getOtherLocationsQtyAvailable: number
}

export const useCartProduct = ({
  product,
  vehicle,
  sellerDetails = MY_PLACE_SELLER,
  locationId,
}: ICartProductProps): ICartProductData => {
  const sellerDetailsRef = useRef(sellerDetails)
  sellerDetailsRef.current = sellerDetails

  const [requestedSpecificStore, setRequestedSpecificStore] =
    useState<boolean>(false)
  const [priceSpinner, setPriceSpinner] = useState<boolean>(
    product.isLoadingPrices || false
  )

  const productAddedToCartMsg = Translate('partWasAddedToYourCart', [
    product.description || '',
  ])

  const cartLink = {
    text: Translate('viewCart'),
    action: (): void => {
      history.navigate('/cart')
    },
  }

  const { perCarQty } = product

  const uiStore = useUiStateStore()

  const searchStore = useSearchStore()
  const userStore = useUserStore()
  const multiLocation = uiStore.isMultiLocationEnabled()

  const { lastNonCachedSearch } = useSearchStore()

  const coverageKey = sellerDetails?.coverageKey ?? ''

  const currentVehicle: Vehicle = useMemo<Vehicle>(() => {
    if (vehicle && isVehicleComplete(vehicle)) {
      return vehicle
    }

    return searchStore.currentVehicle?.engine?.id
      ? searchStore.currentVehicle
      : miscellaneousVehicle
  }, [vehicle, searchStore.currentVehicle])

  const totalProductQtyInCart = useSelector((state: RootState) =>
    getInCartProductQty(state, currentVehicle, coverageKey, product)
  )

  const [numInCart, setNumInCart] = useState(totalProductQtyInCart)

  const orderIfNotAvailable = useMemo<boolean>(() => {
    return userStore?.preferences?.findit_orderIfNotAvail === 'true'
  }, [userStore?.preferences?.findit_orderIfNotAvail])

  // const inCart = initialProductQty > 0
  const inCart = useSelector((state: RootState) =>
    isProductInCart(state, product, currentVehicle, sellerDetails.coverageKey)
  )

  const getInCartQtyByLocation = useCallback(
    (location: ProductLocationModel): number => {
      let total = 0
      const selectedVehicle: SellerCartVehicle | undefined = store
        .getState()
        .cart.cartData.vehicles.find((v) =>
          isSameVehicle(v.vehicle, currentVehicle)
        )

      if (!selectedVehicle) {
        return total
      }
      selectedVehicle.sellers.forEach((seller) => {
        seller.locations.forEach((loc) => {
          if (loc.locationId.toString() === location.locationId.toString()) {
            loc.parts.forEach((currentPart) => {
              if (productsMatch(currentPart, product)) {
                total += currentPart.quantityRequested
              }
            })
          }
        })
      })

      return total
    },
    [currentVehicle, product]
  )

  const sellerCart = useSelector((state: RootState) => {
    const cartVehicle = state.cart.cartData.vehicles.find((v) =>
      isSameVehicle(v.vehicle, currentVehicle)
    )
    if (!cartVehicle) {
      return undefined
    }
    return cartVehicle.sellers.find(
      (seller) => seller.coverageKey === sellerDetails.coverageKey
    )
  })

  const locations = useMemo(() => {
    return product?.location ?? []
  }, [product?.location])

  const initialActiveLocation = useMemo(():
    | ProductLocationModel
    | undefined => {
    if (inCart) {
      if (sellerCart) {
        const locationWithPart = sellerCart.locations.find((cartLocation) => {
          return cartLocation.parts.find((cartPart) =>
            productsMatch(cartPart, product)
          )
        })

        if (locationWithPart) {
          const partLocation = product.location.find(
            (location) =>
              locationWithPart.locationId.toString() ===
              location.locationId.toString()
          )
          if (partLocation) {
            return { ...partLocation }
          }
        }
      }
    }
    if (locationId) {
      return locations.find(
        (location) => location.locationId.toString() === locationId.toString()
      )
    }
    return searchStore.getUpdatedPartInfoDetails(product)?.updatedLocation &&
      !inCart
      ? searchStore.getUpdatedPartInfoDetails(product)?.updatedLocation
      : getStartLocation(locations, 1, inCart)
  }, [inCart, locationId, locations, product, searchStore, sellerCart])

  const [activeLocation, setActiveLocation] = useState<
    ProductLocationModel | undefined
  >(initialActiveLocation)

  const handleAutoChangeLocation = useCallback(
    (newLocation: ProductLocationModel) => {
      setActiveLocation(newLocation)
      ShowNotification({
        message: 'changedStoreDueToAvailability',
        variant: ToastNotificationVariant.SUCCESS,
      })
    },
    []
  )

  const setInitialValueToOne =
    userStore?.preferences?.display_perCarQtyOne === 'true'

  const initialPerCarQty = setInitialValueToOne ? 1 : perCarQty || 0

  const displayMinQty =
    (activeLocation?.displayMinQty > activeLocation?.qtyAvailable
      ? activeLocation?.qtyAvailable
      : activeLocation?.displayMinQty) ?? 1

  const initialValueIfNotInCart: number =
    lastNonCachedSearch === NonCachedSearchType.searchByMultiPartsInquiry ||
    lastNonCachedSearch === NonCachedSearchType.searchBySpecificParts
      ? activeLocation?.buyQty || 0
      : Math.max(
          1,
          displayMinQty,
          activeLocation?.buyIncrement || 0,
          initialPerCarQty
        )

  const initialSelectorQty: number =
    numInCart > 0
      ? numInCart
      : searchStore.getUpdatedPartInfoDetails(product)?.updatedQty > 0
        ? searchStore.getUpdatedPartInfoDetails(product)?.updatedQty
        : initialValueIfNotInCart

  const [currentQty, setCurrentQty] = useState<number>(initialSelectorQty)

  const inCartText = `${totalProductQtyInCart} ${Translate('inCart')}`

  const inCartRef = useRef(false)

  useEffect(() => {
    inCartRef.current = inCart
  }, [inCart])

  const lastValidLocationRef = useRef<ProductLocationModel | undefined>(
    activeLocation
  )

  useEffect(() => {
    if (!activeLocation) return

    const maxAllowed = activeLocation.qtyAvailable

    if (
      orderIfNotAvailable || // allow anything
      currentQty <= maxAllowed // valid within stock
    ) {
      lastValidLocationRef.current = activeLocation
    }
  }, [currentQty, activeLocation, orderIfNotAvailable])

  // Tracks the last successfully committed qty — not updated on keystrokes,
  // only when a quantity change succeeds. Used to revert on failure.
  const lastCommittedQtyRef = useRef<number>(initialSelectorQty)

  useEffect(() => {
    setActiveLocation(initialActiveLocation)
  }, [initialActiveLocation])

  useEffect(() => {
    setNumInCart(totalProductQtyInCart)
  }, [totalProductQtyInCart])

  const updateQuantity = useCallback(
    async (
      qty: number,
      location: ProductLocationModel
    ): Promise<UpdateQuantityResponse> => {
      setPriceSpinner(true)

      const inCart = inCartRef.current

      const prevQty = lastCommittedQtyRef.current
      const currentSellerDetails = sellerDetailsRef.current

      const { product: updatedProduct, hasTimeout } = await updatePartInfo(
        product,
        qty,
        location,
        currentSellerDetails.coverageKey
      )

      if (hasTimeout) {
        setPriceSpinner(false)
        ShowNotification({
          message: 'pleaseTryAgainAndContactYourPartsSupplier',
          variant: ToastNotificationVariant.ERROR,
        })
        setCurrentQty(prevQty)
        return {
          prevLocation: location,
          newLocation: location,
          updatedLocations: product.location,
        }
      }

      const updatedLocation = getLocationById(
        updatedProduct,
        location.locationId
      )

      const orderIfNotAvailable =
        StoreInstances.userStore?.preferences?.findit_orderIfNotAvail === 'true'

      // updating searched results with availability end point location update
      const searchedResultsParts =
        StoreInstances.searchStore.searchResults?.parts || []

      searchedResultsParts.forEach((part) => {
        if (productsMatch(part, updatedProduct)) {
          part.location = updatedProduct.location
        }
      })
      setPriceSpinner(false)

      // MPV3-3428 update location when product is in cart.
      const locationWithQty = autoSelectLocation(
        updatedProduct.location ?? [],
        qty
      )
      const locationToSet = locationId ? updatedLocation : locationWithQty // Do not auto choose location if the hook is called from the location selector drawer.

      if (!orderIfNotAvailable) {
        const { qtyAvailable = 0 } = locationToSet
        if (qty > qtyAvailable) {
          ShowNotification({
            message: 'productCantBeFulfilled',
            variant: ToastNotificationVariant.ERROR,
          })

          setPriceSpinner(false)
          setCurrentQty(prevQty)
          setActiveLocation(location)

          return {
            prevLocation: updatedLocation,
            newLocation: updatedLocation,
            updatedLocations: updatedProduct.location,
          }
        }
      }

      setCurrentQty(qty)
      lastCommittedQtyRef.current = qty

      const allowOrderIfNotAvailable = allowOrderWithoutAvailableInventory(
        locationToSet,
        StoreInstances.userStore.preferences.findit_orderIfNotAvail === 'true'
      )

      const minQty = Math.min(locationToSet?.qtyAvailable, qty)
      const chooseMinQty = multiLocation && !allowOrderIfNotAvailable
      const autoLocationChange = locationId
        ? false
        : !requestedSpecificStore || !allowOrderIfNotAvailable

      // For multilocation, auto location change is disabled, so the quantity available has to be enforced unless the location is primary and order if not available is allowed
      const qtyToSet = chooseMinQty ? minQty : qty

      setActiveLocation(locationToSet)

      if (!inCart) {
        if (locationToSet.locationId !== updatedLocation?.locationId) {
          ShowNotification({
            message: 'changedStoreDueToAvailability',
            variant: ToastNotificationVariant.SUCCESS,
          })
        }
      } else {
        // Check if this is a quantity update from the more locations drawer
        // vs a new item being added to cart
        const isFromLocationDrawer = !!locationId
        const inCartAtThisLocation = getInCartQtyByLocation(locationToSet) > 0

        // Only update cart if:
        // 1. Item is already in cart at this location, OR
        // 2. This is NOT from the location drawer (normal quantity selector usage)
        if (inCartAtThisLocation || !isFromLocationDrawer) {
          try {
            await setQtyAtLocationBulk(
              [
                {
                  product: updatedProduct,
                  locationId: locationToSet?.locationId,
                  quantity: qtyToSet,
                  onLocationChange: (newLoc) => {
                    // newLocationId = newLoc.locationId
                    handleAutoChangeLocation(newLoc)
                  },
                  autoLocationChange,
                  vehicle: currentVehicle,
                  seller: sellerDetails,
                },
              ],
              undefined,
              // Skip calling fulfilment.
              false
            )
            if (
              locationToSet.locationId.toString() !==
              updatedLocation?.locationId.toString()
            ) {
              // Remove the previous part in case of auto switch location.
              store.dispatch(
                removeProductThunk(
                  {
                    product,
                    quantity: ABSOLUTE_MAX_QUANTITY,
                    locationId: updatedLocation.locationId.toString(),
                    vehicle: currentVehicle,
                    seller: sellerDetails,
                    removeFromAllLocations: false,
                    showToastMessage: false,
                  },
                  // Skip calling fulfilment.
                  false
                )
              )
            }
            /* eslint-disable  @typescript-eslint/no-explicit-any */
          } catch (e: any) {
            ShowNotification({
              message: e.message,
              variant: ToastNotificationVariant.ERROR,
            })
            setCurrentQty(prevQty)
          } finally {
            // Calling fulfilment as we are skipping it above.
            store.dispatch(updateBundleFulfilmentThunk())
          }
        }
      }

      return {
        prevLocation: updatedLocation,
        newLocation: locationToSet,
        updatedLocations: updatedProduct.location,
      }
    },
    [
      currentVehicle,
      getInCartQtyByLocation,
      handleAutoChangeLocation,
      locationId,
      multiLocation,
      product,
      requestedSpecificStore,
      sellerDetails,
    ]
  )

  const onChangeLocation = async (
    selectedLocation: ProductLocationModel
  ): Promise<void> => {
    setActiveLocation(selectedLocation)
    if (!inCart || selectedLocation.qtyAvailable >= currentQty)
      ShowNotification({
        message: 'locationChangedTo',
        variant: ToastNotificationVariant.SUCCESS,
        messageParams: [selectedLocation.called],
      })
    // if (
    //   selectedLocation.priceBreaks[0].unitCost !=
    //   activeLocation.priceBreaks[0].unitCost
    // ) {
    //   uiStore.displaySuccessNotification('costChangedFrom', undefined, [
    //     `$${roundYourCostToTwoDecimals(activeLocation?.priceBreaks[0]?.unitCost || 0)}`,
    //     `$${roundYourCostToTwoDecimals(selectedLocation?.priceBreaks[0]?.unitCost || 0)}`,
    //   ])
    // }
    setRequestedSpecificStore(true)
    try {
      if (inCart) {
        await setQtyAtLocationBulk([
          {
            product,
            locationId: selectedLocation.locationId,
            autoLocationChange: !allowOrderWithoutAvailableInventory(
              selectedLocation,
              StoreInstances.userStore.preferences.findit_orderIfNotAvail ===
                'true'
            ),
            quantity: currentQty,
            vehicle: currentVehicle,
            onLocationChange: (location) => {
              setActiveLocation(location)
              ShowNotification({
                message: 'locationChangedTo',
                variant: ToastNotificationVariant.SUCCESS,
                messageParams: [location.called],
              })
            },
            seller: sellerDetails,
          },
        ])
      }
    } catch (error: any) {
      ShowNotification({
        message: error.message,
        variant: ToastNotificationVariant.ERROR,
      })
    }
  }

  const showLocationSelector = () => {
    if (multiLocation) {
      show(AddToCartDrawer, {
        searchStore,
        seller: sellerDetails,
        activeLocation,
        product,
        currentVehicle,
      })
    } else {
      show(StoreSelectorDrawer, {
        product,
        activeLocation,
        onChangeLocation,
      })
    }
  }

  const yourCostAfterPriceBreaks = (): number => {
    const priceBreaks = activeLocation?.priceBreaks || []
    if (priceBreaks.length > 0) {
      const lastPriceBreak = priceBreaks[priceBreaks.length - 1]
      for (const priceBreak of priceBreaks || []) {
        if (
          priceBreak?.minQty <= currentQty &&
          priceBreak?.maxQty >= currentQty
        ) {
          return priceBreak?.unitCost
        }
        // checking minQty for lastPriceBreak, as maxQty returns 0 for some parts
        if (lastPriceBreak?.minQty && currentQty >= lastPriceBreak?.minQty) {
          return lastPriceBreak?.unitCost
        }
      }
    }
    return activeLocation ? activeLocation.cost : 0
  }

  const isZeroCost =
    yourCostAfterPriceBreaks() === 0 && activeLocation?.coreCost === 0
  const disableAddToCart =
    isZeroCost ||
    priceSpinner ||
    activeLocation?.locType === LocationType.VIEW_ONLY

  const handleAddVehiclePartToCart = async (
    vehicle: Vehicle,
    location?: ProductLocationModel,
    qty?: number
  ): Promise<void> => {
    const locationId = location?.locationId || activeLocation?.locationId || ''
    const updatedProduct = { ...product, location: [...product.location] }
    const locationIndex = updatedProduct.location.findIndex(
      (loc) => loc.locationId === activeLocation?.locationId
    )
    if (locationIndex >= 0 && activeLocation) {
      updatedProduct.location[locationIndex] = {
        ...activeLocation,
        isSelected: true,
      }
    }

    try {
      await setQtyAtLocationBulk([
        {
          product: updatedProduct,
          locationId,
          quantity: qty ?? currentQty,
          vehicle,
          autoLocationChange: location
            ? false
            : !requestedSpecificStore ||
              !allowOrderWithoutAvailableInventory(
                location ?? activeLocation,
                StoreInstances.userStore.preferences.findit_orderIfNotAvail ===
                  'true'
              ),
          onLocationChange: (location) => {
            setActiveLocation(location)
          },
          seller: sellerDetails,
        },
      ])
      setNumInCart(totalProductQtyInCart)
      hawkSearchProvider.trackEvent(
        'Add2Cart',
        prepareEventData('Add2Cart', {
          productId:
            product.allianceProductId ||
            `${product.lineCode} ${product.partNumber}`,
          price: activeLocation?.cost,
          quantity: qty ?? currentQty,
          vehicle: currentVehicle,
        })
      )
      ShowNotification({
        message: productAddedToCartMsg,
        variant: ToastNotificationVariant.SUCCESS,
        link: cartLink,
      })
    } catch (e: any) {
      ShowNotification({
        message: e.message,
        variant: ToastNotificationVariant.ERROR,
      })
    }
  }

  const inStockSomewhere = useMemo<boolean>(() => {
    const globalQtyAvailable = product?.location
      ?.map((l) => l.qtyAvailable)
      .reduce((a, b) => a + b, 0)

    return globalQtyAvailable > 0
  }, [product?.location])

  const invDisplayState = useMemo<InventoryDisplay>(() => {
    const { qtyAvailable = 0 } = activeLocation || {}

    let invDisplayState = InventoryDisplay.CALL_FOR_AVAILABILITY
    if (qtyAvailable > 0) {
      invDisplayState = InventoryDisplay.AVAILABLE_AT_LOCATION
    } else if (inStockSomewhere || orderIfNotAvailable) {
      invDisplayState = InventoryDisplay.SHOW_ZERO
    }
    return invDisplayState
  }, [activeLocation, inStockSomewhere, orderIfNotAvailable])

  const getNonPrimaryLocationsQtyAvailable = useMemo(
    () =>
      (product?.location ?? [])
        .filter((loc) => loc.locType !== LocationType.PRIMARY)
        .reduce((sum, loc) => sum + (loc.qtyAvailable || 0), 0),
    [product?.location]
  )

  const getPrimaryLocationQtyAvailable = useMemo(() => {
    const primary = (product?.location ?? []).find(
      (loc) => loc.locType === LocationType.PRIMARY
    )
    return { qty: primary?.qtyAvailable || 0, called: primary?.called || '' }
  }, [product?.location])

  const getOtherLocationsQtyAvailable = useMemo(
    () =>
      (product?.location ?? [])
        .filter(
          (loc) =>
            loc.locType !== LocationType.PRIMARY &&
            loc.locationId !== activeLocation?.locationId
        )
        .reduce((sum, loc) => sum + (loc.qtyAvailable || 0), 0),
    [product?.location, activeLocation]
  )

  return {
    updateQuantity,
    numInCart,
    setNumInCart,
    inCart,
    activeLocation,
    currentQty,
    setCurrentQty,
    showLocationSelector,
    handleAddVehiclePartToCart,
    priceSpinner,
    initialSelectorQty,
    inCartText,
    disableAddToCart,
    isZeroCost,
    yourCostAfterPriceBreaks,
    setActiveLocation,
    setPriceSpinner,
    product,
    getInCartQtyByLocation,
    onChangeLocation,
    inStockSomewhere,
    orderIfNotAvailable,
    invDisplayState,
    getNonPrimaryLocationsQtyAvailable,
    getPrimaryLocationQtyAvailable,
    getOtherLocationsQtyAvailable,
  }
}
